/**
 * Favorites Store using Zustand
 * Replaces the old FavoritesStore with modern React state management
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { devtools, persist } from 'zustand/middleware';
import { FAVORITE_SECTION_NAME, LOCAL_STORAGE_FAVORITE_KEY } from '@repo/common';
import { externalCommunication } from '../../../core/services/communication/ExternalCommunication';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import type IGameSettings from '../../../types/models/IGameSettings';

// PERFORMANCE OPTIMIZED: Debounce utility for smooth rapid toggling
const debounce = <A extends unknown[], T extends (...args: A[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// PERFORMANCE OPTIMIZED: Batch localStorage updates for better UX
const batchedLocalStorageUpdate = debounce((gameKeys) => {
  try {
    const storageKey = LOCAL_STORAGE_FAVORITE_KEY;
    if (gameKeys.length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, gameKeys.join(','));
    }
  } catch (error) {
    console.warn('Failed to save favorites to localStorage:', error);
  }
}, 150); // 150ms debounce for smooth rapid clicking

interface FavoritesState {
  favorites: ISectionSettings;
  favoriteGameKeys: string[];
  
  // Actions
  setFavorites: (favorites: ISectionSettings) => void;
  toggleFavorite: (gameKey: string, game?: IGameSettings) => void;
  addFavorite: (game: IGameSettings) => void;
  removeFavorite: (gameKey: string) => void;
  clearFavorites: () => void;
  isFavorite: (gameKey: string) => boolean;
  loadFavoritesFromStorage: (playerId: string) => void;
  saveFavoritesToStorage: (playerId: string, gameKeys: string[]) => void;
  rebuildFavoritesFromSections: (allSections: ISectionSettings[]) => void;
  debouncedRebuildFavorites: (allSections: ISectionSettings[]) => void;
}

const DEFAULT_FAVORITES: ISectionSettings = {
  name: FAVORITE_SECTION_NAME,
  jackpotName: null,
  icon: 'favorites_icon',
  banner: false,
  games: [],
  gridBlocks: 0,
};

export const useFavoritesStore = createWithEqualityFn<FavoritesState>()(
  devtools(
    persist(
      (set, get): FavoritesState => ({
        favorites: DEFAULT_FAVORITES,
        favoriteGameKeys: [],
        
        setFavorites: (favorites) => {
          const gameKeys = favorites.games.map(game => game.gameKey);
          set({ 
            favorites, 
            favoriteGameKeys: gameKeys 
          }, false, 'setFavorites');
        },
        
        toggleFavorite: (gameKey, game) => {
          const state = get();
          const isFavorite = state.favoriteGameKeys.includes(gameKey);

          if (isFavorite) {
            state.removeFavorite(gameKey);
            externalCommunication.sendGameRemovedFromFavorites(gameKey);
          } else if (game) {
            state.addFavorite(game);
            externalCommunication.sendGameAddedToFavorites(gameKey);
          }
        },
        
        addFavorite: (game) => {
          set((state) => {
            if (state.favoriteGameKeys.includes(game.gameKey)) {
              return state; // Already a favorite
            }
            
            const newGames = [...state.favorites.games, game];
            const newGameKeys = [...state.favoriteGameKeys, game.gameKey];
            
            // PERFORMANCE OPTIMIZED: Batch localStorage updates for smooth UX
            batchedLocalStorageUpdate(newGameKeys);
            
            return {
              favorites: {
                ...state.favorites,
                games: newGames,
                gridBlocks: Math.ceil(newGames.length / 6), // Assuming 6 games per row
              },
              favoriteGameKeys: newGameKeys,
            };
          }, false, 'addFavorite');
        },
        
        removeFavorite: (gameKey) => {
          set((state) => {
            const newGames = state.favorites.games.filter(game => game.gameKey !== gameKey);
            const newGameKeys = state.favoriteGameKeys.filter(key => key !== gameKey);
            
            // PERFORMANCE OPTIMIZED: Batch localStorage updates for smooth UX
            batchedLocalStorageUpdate(newGameKeys);
            
            return {
              favorites: {
                ...state.favorites,
                games: newGames,
                gridBlocks: Math.ceil(newGames.length / 6),
              },
              favoriteGameKeys: newGameKeys,
            };
          }, false, 'removeFavorite');
        },
        
        clearFavorites: () => {
          try {
            localStorage.removeItem(LOCAL_STORAGE_FAVORITE_KEY);
          } catch (error) {
            console.warn('Failed to clear favorites from localStorage:', error);
          }
          
          set({
            favorites: DEFAULT_FAVORITES,
            favoriteGameKeys: [],
          }, false, 'clearFavorites');
        },
        
        isFavorite: (gameKey) => {
          return get().favoriteGameKeys.includes(gameKey);
        },
        
        loadFavoritesFromStorage: (playerId) => {
          try {
            const storageKey = `${LOCAL_STORAGE_FAVORITE_KEY}_${playerId}`;
            const favoritesString = localStorage.getItem(storageKey) || localStorage.getItem(LOCAL_STORAGE_FAVORITE_KEY);
            
            if (favoritesString) {
              const gameKeys = favoritesString.split(',').filter(key => key.trim() !== '');
              set({ favoriteGameKeys: gameKeys }, false, 'loadFavoritesFromStorage');
            }
          } catch (error) {
            console.warn('Failed to load favorites from localStorage:', error);
          }
        },
        
        saveFavoritesToStorage: (playerId, gameKeys) => {
          try {
            const storageKey = playerId ? `${LOCAL_STORAGE_FAVORITE_KEY}_${playerId}` : LOCAL_STORAGE_FAVORITE_KEY;
            if (gameKeys.length === 0) {
              localStorage.removeItem(storageKey);
            } else {
              localStorage.setItem(storageKey, gameKeys.join(','));
            }
          } catch (error) {
            console.warn('Failed to save favorites to localStorage:', error);
          }
        },
        
        rebuildFavoritesFromSections: (allSections) => {
          const state = get();
          const favoriteGames: IGameSettings[] = [];
          
          // PERFORMANCE OPTIMIZED: Use Map for O(1) game lookups instead of O(n)
          const gameKeyToGameMap = new Map<string, IGameSettings>();
          allSections.forEach(section => {
            section.games.forEach(game => {
              gameKeyToGameMap.set(game.gameKey, game);
            });
          });
          
          // Find all games that match our favorite game keys efficiently
          state.favoriteGameKeys.forEach(gameKey => {
            const game = gameKeyToGameMap.get(gameKey);
            if (game) {
              favoriteGames.push(game);
            }
          });
          
          set({
            favorites: {
              ...DEFAULT_FAVORITES,
              games: favoriteGames,
              gridBlocks: Math.ceil(favoriteGames.length / 6),
            }
          }, false, 'rebuildFavoritesFromSections');
        },
        
        // PERFORMANCE OPTIMIZED: Debounced version for smooth rapid toggling
        debouncedRebuildFavorites: debounce((allSections) => {
          get().rebuildFavoritesFromSections(allSections as ISectionSettings[]);
        }, 100), // 100ms debounce for rebuilding favorites section
      }),
      {
        name: 'lobby2-favorites-storage',
        partialize: (state) => ({
          favoriteGameKeys: state.favoriteGameKeys,
        }),
      }
    ),
    { name: 'FavoritesStore' }
  )
);

// Selector hooks
// fallow-ignore-next-line unused-export
export const useFavoriteGames = () => useFavoritesStore((state) => state.favorites.games);
// fallow-ignore-next-line unused-export
export const useFavoriteGameKeys = () => useFavoritesStore((state) => state.favoriteGameKeys);

// PERFORMANCE OPTIMIZED: Only triggers re-render when specific game's favorite status changes
export const useIsFavorite = (gameKey: string) => useFavoritesStore(
  (state) => state.favoriteGameKeys.includes(gameKey),
  // Custom equality function to prevent re-renders when other games change
  (oldValue, newValue) => oldValue === newValue
);
